import React, { useMemo, useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { all_routes } from "../../router/all_routes";
import Api from "../../../core/data/redux/utils/apiCall";
import { API_URLS } from "../../../core/data/redux/apis/urls";
import { toast } from "react-toastify";
import Table from "../../../core/common/dataTable/index";

type ProbationCycle = {
  id: string | number;
  title: string;
  financialYear: string;
  quarter: string;
  status: "INITIATED" | "CALCULATED" | "REVIEWED" | "APPROVED" | "COMPLETED" | string;
  createdAt?: string;
};

const DashboardTile = ({
  title,
  icon,
  count,
  colorClass = "text-primary",
  bgColor = "rgba(var(--bs-primary-rgb), 0.04)",
}: {
  title: string;
  icon?: string;
  count?: string | number;
  colorClass?: string;
  bgColor?: string;
}) => {
  return (
    <div className="col-xxl-3 col-xl-3 col-lg-3 col-md-6 col-sm-6 col-12 mb-4">
      <div
        className="card h-100 border-0 shadow-sm hover-card"
        style={{
          borderRadius: "16px",
          transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
          overflow: "hidden",
          backgroundColor: "#fff",
          boxShadow: "0 4px 20px rgba(0, 0, 0, 0.02)"
        }}
      >
        <div className="card-body d-flex align-items-center p-4">
          <div
            className="d-flex align-items-center justify-content-center me-3 rounded-circle"
            style={{
              width: "56px",
              height: "56px",
              backgroundColor: bgColor,
              border: "1px solid rgba(0,0,0,0.02)",
              transition: "transform 0.3s ease"
            }}
          >
            {icon && <i className={`ti ${icon} ${colorClass}`} style={{ fontSize: "28px" }}></i>}
          </div>
          <div>
            <div className="text-muted small mb-1 fw-medium" style={{ fontSize: "13px", letterSpacing: "0.2px" }}>
              {title}
            </div>
            <div className="h3 fw-bold mb-0 text-dark" style={{ letterSpacing: "-0.5px" }}>
              {count}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const HARDCODED_CYCLES: ProbationCycle[] = [
  {
    id: 1,
    title: "Probation Confirmation - Q1 2025-26",
    financialYear: "2025-26",
    quarter: "Q1",
    status: "APPROVED",
    createdAt: "2025-06-15T08:00:00.000Z"
  },
  {
    id: 2,
    title: "Probation Confirmation - Q2 2025-26",
    financialYear: "2025-26",
    quarter: "Q2",
    status: "REVIEWED",
    createdAt: "2025-09-18T09:30:00.000Z"
  },
  {
    id: 3,
    title: "Probation Confirmation - Q3 2025-26",
    financialYear: "2025-26",
    quarter: "Q3",
    status: "CALCULATED",
    createdAt: "2025-12-22T10:15:00.000Z"
  },
  {
    id: 4,
    title: "Probation Confirmation - Q4 2025-26",
    financialYear: "2025-26",
    quarter: "Q4",
    status: "INITIATED",
    createdAt: "2026-03-10T11:00:00.000Z"
  }
];

const ProbationDashboard: React.FC = () => {
  const navigate = useNavigate();
  const [allCycles, setAllCycles] = useState<ProbationCycle[]>(HARDCODED_CYCLES);
  const [cycles, setCycles] = useState<ProbationCycle[]>(HARDCODED_CYCLES);
  const [quarterFilter, setQuarterFilter] = useState<string>("All");
  const [loading, setLoading] = useState<boolean>(false);
  const [truncating, setTruncating] = useState<boolean>(false);

  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10
  });

  // Reset pagination on filter change
  useEffect(() => {
    setPagination(prev => ({ ...prev, current: 1 }));
  }, [quarterFilter]);

  const fetchCycles = async () => {
    setLoading(true);
    try {
      // Filter locally based on quarterFilter
      if (quarterFilter === "All") {
        setCycles(allCycles);
      } else {
        setCycles(allCycles.filter(c => c.quarter === quarterFilter));
      }
    } catch (e) {
      console.error("Failed to filter confirmation cycles", e);
    } finally {
      setLoading(false);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    fetchCycles();
  }, [quarterFilter, allCycles]);

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const handleTruncate = async () => {
    if (!window.confirm("Warning: This will truncate/reset all probation and confirmation data. Are you sure you want to proceed?")) {
      return;
    }
    setTruncating(true);
    try {
      setAllCycles([]);
      setCycles([]);
      toast.success("Probation cycles truncated successfully.");
    } catch (e: any) {
      console.error("Truncate failed", e);
      toast.error("Truncation failed");
    } finally {
      setTruncating(false);
    }
  };

  const getStatusBadge = (status: string) => {
    const s = (status || "").toUpperCase();
    switch (s) {
      case "INITIATED":
        return <span className="badge bg-soft-info text-info border border-info-subtle px-2.5 py-1">Initiated</span>;
      case "CALCULATED":
        return <span className="badge bg-soft-warning text-warning border border-warning-subtle px-2.5 py-1">Calculated</span>;
      case "REVIEWED":
        return <span className="badge bg-soft-primary text-primary border border-primary-subtle px-2.5 py-1">Committee Reviewed</span>;
      case "COMPETENT_AUTHORITY_APPROVAL":
        return <span className="badge bg-soft-primary text-primary border border-primary-subtle px-2.5 py-1">CA Pending</span>;
      case "APPROVED":
        return <span className="badge bg-soft-success text-success border border-success-subtle px-2.5 py-1">Approved</span>;
      case "COMPLETED":
        return <span className="badge bg-soft-secondary text-secondary border border-secondary-subtle px-2.5 py-1">Office Order Issued</span>;
      default:
        return <span className="badge bg-soft-dark text-dark border border-dark-subtle px-2.5 py-1">{status || "Unknown"}</span>;
    }
  };

  const stats = useMemo(() => {
    return {
      total: allCycles.length,
      initiated: allCycles.filter(c => c.status === "INITIATED").length,
      calculated: allCycles.filter(c => c.status === "CALCULATED").length,
      approved: allCycles.filter(c => c.status === "APPROVED" || c.status === "COMPLETED").length,
    };
  }, [allCycles]);

  const parsedCycles = useMemo(() => {
    return cycles.map((c, idx) => ({
      ...c,
      key: c.id ? String(c.id) : String(idx),
    }));
  }, [cycles]);

  // Extract unique quarters dynamically
  const uniqueQuarters = useMemo(() => {
    const quartersSet = new Set(allCycles.map(c => c.quarter).filter(Boolean));
    return Array.from(quartersSet).sort();
  }, [allCycles]);

  const formatDate = (val: string) => {
    if (!val || val === "N/A") return "N/A";
    try {
      const d = new Date(val);
      if (isNaN(d.getTime())) return val;
      const day = String(d.getDate()).padStart(2, "0");
      const month = String(d.getMonth() + 1).padStart(2, "0");
      const year = d.getFullYear();
      return `${day}/${month}/${year}`;
    } catch (e) {
      return val;
    }
  };

  const getResumeStep = (status: string) => {
    const s = String(status || "").trim().toUpperCase();
    switch (s) {
      case "INITIATED":
        return 2;
      case "PVR_CLEARED":
      case "PVR_CLEAR":
      case "PVR_STATUS_SET":
      case "PVR_CALCULATED":
      case "PVR_DONE":
        return 3;
      case "ELIGIBILITY_CALCULATED":
      case "CALCULATED":
      case "CANDIDATES_CALCULATED":
        return 4;
      case "WR_GRADING":
      case "WR_CLEARED":
      case "WR_CLEAR":
      case "WR_SUITABLE":
      case "WR_CALCULATED":
      case "WR_STATUS_SET":
        return 5;
      case "DAR_CLEARED":
      case "DAR_CLEAR":
      case "DAR_STATUS_SET":
      case "DAR_CALCULATED":
      case "DAR_SUITABLE":
        return 6;
      case "VIGILANCE_CLEARED":
      case "VIGILANCE_CLEAR":
      case "VIGILANCE_STATUS_SET":
      case "VIGILANCE_CALCULATED":
      case "VIGILANCE_UPDATED":
      case "ELIGIBLE_GENERATED":
        return 7;
      default:
        return 2;
    }
  };

  // Define Table Columns
  const columns = useMemo(() => {
    return [
      {
        title: "Sr. No.",
        dataIndex: "srNo",
        render: (_: any, __: any, idx: number) => ((pagination.current - 1) * pagination.pageSize) + idx + 1,
        width: 70
      },
      {
        title: "Cycle ID",
        dataIndex: "id",
        className: "fw-medium text-dark",
        render: (val: any) => `#${val}`
      },
      {
        title: "Title / Name",
        dataIndex: "title",
        render: (val: string, record: any) => {
          let clean = val || `Probation Cycle #${record.id}`;
          if (clean.includes("[")) {
            clean = clean.substring(0, clean.indexOf("[")).trim();
          }
          return (
            <div>
              <div className="fw-semibold text-dark">{clean}</div>
              <span className="small text-muted">Created: {record.createdAt ? formatDate(record.createdAt) : "N/A"}</span>
            </div>
          );
        }
      },
      {
        title: "Financial Year",
        dataIndex: "financialYear",
        align: "center",
        render: (val: string) => <span className="badge bg-light text-dark border">{val}</span>
      },
      {
        title: "Pay Scales",
        dataIndex: "grade",
        render: (_: any, record: any) => {
          let scalesList: string[] = [];
          try {
            const parsed = typeof record.grade === "string" ? JSON.parse(record.grade) : record.grade;
            if (Array.isArray(parsed)) {
              scalesList = parsed.map((s: any) => String(s).trim());
            } else if (typeof parsed === "string") {
              scalesList = parsed.split(",").map((s: string) => s.trim());
            }
          } catch {
            if (typeof record.grade === "string") {
              scalesList = record.grade.split(",").map((s: string) => s.trim());
            }
          }
          if (scalesList.length === 0 && record.title && record.title.includes("[")) {
            const match = record.title.match(/\[(.*?)\]/);
            if (match && match[1]) {
              scalesList = match[1].split(",").map((s: string) => s.trim());
            }
          }
          const cleanScales = scalesList.filter(Boolean);
          if (cleanScales.length === 0) return <span className="text-muted">N/A</span>;
          return (
            <div className="d-flex flex-wrap gap-1 align-items-center" style={{ maxWidth: "350px" }}>
              {cleanScales.map((scale: string, idx: number) => (
                <span
                  key={idx}
                  className="badge bg-primary-transparent text-primary fw-medium"
                  style={{ fontSize: "11px", padding: "3px 6px", borderRadius: "5px", border: "1px solid rgba(59, 130, 246, 0.2)" }}
                >
                  {scale}
                </span>
              ))}
            </div>
          );
        }
      },
      {
        title: "Status",
        dataIndex: "status",
        align: "center",
        render: (val: string) => getStatusBadge(val)
      },
      {
        title: "Actions",
        dataIndex: "actions",
        align: "right",
        render: (_: any, record: any) => {
          const s = String(record?.status || "").trim().toUpperCase();
          const isWizardStep = !(s === "SCREENING_COMMITTEE" || s === "REVIEWED" || s === "COMMITTEE_REVIEW" || s === "COMPETENT_AUTHORITY_APPROVAL" || s === "APPROVED" || s === "COMPLETED" || s === "OFFICE_ORDER_GENERATED");
          return (
            <div className="d-flex justify-content-end gap-2">
              <Link
                to={`${all_routes.probation.dashboard}?cycleId=${record.id}`}
                className="btn btn-icon btn-sm btn-outline-primary"
                title="View Candidate List"
              >
                <i className="ti ti-users"></i>
              </Link>
              {isWizardStep && (
                <Link
                  to={`${all_routes.probation.newCycle}?cycleId=${record.id}&step=${getResumeStep(s)}`}
                  className="btn btn-icon btn-sm btn-outline-warning"
                  title="Resume Wizard Step"
                >
                  <i className="ti ti-player-play"></i>
                </Link>
              )}
              {(s === "SCREENING_COMMITTEE" || s === "REVIEWED" || s === "COMMITTEE_REVIEW") && (
                <Link
                  to={`${all_routes.probation.committeeReview}?cycleId=${record.id}`}
                  className="btn btn-icon btn-sm btn-outline-primary"
                  title="Committee Review"
                >
                  <i className="ti ti-file-search"></i>
                </Link>
              )}
              {s === "COMPETENT_AUTHORITY_APPROVAL" && (
                <Link
                  to={`${all_routes.probation.approvalList}?cycleId=${record.id}`}
                  className="btn btn-icon btn-sm btn-outline-info"
                  title="Competent Authority Approval"
                >
                  <i className="ti ti-checkbox"></i>
                </Link>
              )}
              {(s === "APPROVED" || s === "COMPLETED" || s === "OFFICE_ORDER_GENERATED") && (
                <Link
                  to={`${all_routes.probation.officeOrders}?cycleId=${record.id}`}
                  className="btn btn-icon btn-sm btn-outline-success"
                  title="Generate Office Order"
                >
                  <i className="ti ti-file-text"></i>
                </Link>
              )}
            </div>
          );
        }
      }
    ];
  }, [pagination]);

  return (
    <div className="page-wrapper">
      <div className="content">
        {/* HEADER */}
        <div className="page-header mb-4">
          <div className="row align-items-center">
            <div className="col">
              <h3 className="page-title">
                Probation & Confirmation Dashboard
              </h3>
              <ul className="breadcrumb bg-transparent p-0 mt-1">
                <li className="breadcrumb-item">
                  <Link to={all_routes.adminDashboard}>Dashboard</Link>
                </li>
                <li className="breadcrumb-item active">Probation & Confirmation</li>
              </ul>
            </div>
            <div className="col-auto float-end ms-auto d-flex gap-2">
              <Link to={all_routes.probation.newCycle} className="btn btn-primary">
                <i className="ti ti-plus me-1"></i> New Probation Cycle
              </Link>
            </div>
          </div>
        </div>

        {/* STATISTICS */}
        <div className="row">
          <DashboardTile
            title="Total Cycles"
            icon="ti-folders"
            count={stats.total}
            colorClass="text-primary"
            bgColor="rgba(30, 58, 138, 0.06)"
          />
          <DashboardTile
            title="Initiated Cycles"
            icon="ti-activity"
            count={stats.initiated}
            colorClass="text-info"
            bgColor="rgba(14, 116, 144, 0.06)"
          />
          <DashboardTile
            title="Calculated Cycles"
            icon="ti-cpu"
            count={stats.calculated}
            colorClass="text-warning"
            bgColor="rgba(217, 119, 6, 0.06)"
          />
          <DashboardTile
            title="Approved & Issued"
            icon="ti-circle-check"
            count={stats.approved}
            colorClass="text-success"
            bgColor="rgba(22, 163, 74, 0.06)"
          />
        </div>

        {/* CYCLES LEDGER */}
        <div className="card shadow-sm border-0 mb-4">
          <div className="card-header bg-white py-3 border-bottom-0" style={{ borderRadius: "12px 12px 0 0" }}>
            <div className="d-flex justify-content-between align-items-center flex-wrap gap-3 mb-3">
              <div>
                <h5 className="card-title mb-1 fw-bold text-dark" style={{ fontSize: "16px" }}>
                  Active Probation Cycles
                </h5>
                <p className="text-muted small mb-0">Track steps, candidate counts, and approvals</p>
              </div>
              <div className="d-flex gap-2">
                <select
                  className="form-select form-select-sm"
                  value={quarterFilter}
                  onChange={(e) => setQuarterFilter(e.target.value)}
                  style={{ borderRadius: "8px", width: "140px" }}
                >
                  <option value="All">All Quarters</option>
                  {uniqueQuarters.map(q => (
                    <option key={q} value={q}>
                      {q.startsWith("Q") && !isNaN(Number(q.substring(1)))
                        ? `Quarter ${q.substring(1)}`
                        : q}
                    </option>
                  ))}
                </select>
                <button onClick={fetchCycles} className="btn btn-light btn-sm" title="Refresh List">
                  <i className="ti ti-refresh text-secondary"></i>
                </button>
              </div>
            </div>
          </div>

          <div className="card-body p-0">
            <Table
              columns={columns}
              dataSource={parsedCycles}
              paginationConfig={pagination}
              Selection={false}
              loading={loading}
              scroll={{ x: "max-content" }}
              handlePagination={(pag: any) => {
                setPagination({
                  current: pag.current,
                  pageSize: pag.pageSize
                });
              }}
            />
          </div>
        </div>

        <style>{`
          .btn-icon {
            width: 32px;
            height: 32px;
            display: inline-flex;
            align-items: center;
            justify-content: center;
            padding: 0;
            border-radius: 8px;
            transition: all 0.2s ease;
          }
          .hover-card:hover {
            transform: translateY(-4px);
            box-shadow: 0 12px 24px rgba(0, 0, 0, 0.06) !important;
          }
          .bg-soft-info { background-color: rgba(14, 116, 144, 0.08); }
          .bg-soft-warning { background-color: rgba(217, 119, 6, 0.08); }
          .bg-soft-primary { background-color: rgba(30, 58, 138, 0.08); }
          .bg-soft-success { background-color: rgba(22, 163, 74, 0.08); }
          .bg-soft-secondary { background-color: rgba(107, 114, 128, 0.08); }
          .border-grey { border: 1px solid #d1d5db; }
        `}</style>
      </div>
    </div>
  );
};

export default ProbationDashboard;
